import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Select, Space, Typography, Upload, Alert, message as antdMessage, Tag } from 'antd';
import { RightOutlined, ThunderboltOutlined, UploadOutlined, FileTextOutlined } from '@ant-design/icons';
import concessionariasData from '../../data/concessionarias.json';

const { Option } = Select;
const { Text } = Typography;

const Step4Grid = ({ onNext, onBack, formData }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState(formData?.gridPdfFile ? [formData.gridPdfFile] : []);
    const [distribuidoras, setDistribuidoras] = useState([]);

    // Carregar distribuidoras da ANEEL
    useEffect(() => {
        // Ordenar por estado e nome
        const sorted = [...concessionariasData.distribuidoras].sort((a, b) => {
            if (a.estado !== b.estado) {
                return a.estado.localeCompare(b.estado);
            }
            return a.nome.localeCompare(b.nome);
        });
        setDistribuidoras(sorted);
    }, []);

    React.useEffect(() => {
        if (formData) {
            form.setFieldsValue(formData);
        }
    }, [formData, form]);

    const handleSubmit = async (values) => {
        if (fileList.length === 0) {
            antdMessage.warning('Por favor, faça upload da conta de luz');
            return;
        }

        const data = {
            ...values,
            gridPdfFile: fileList[0],
        };
        onNext(data);
    };

    const uploadProps = {
        beforeUpload: (file) => {
            const isPdf = file.type === 'application/pdf';
            if (!isPdf) {
                antdMessage.error('Apenas arquivos PDF são permitidos');
                return false;
            }
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                antdMessage.error('O arquivo deve ter no máximo 10MB');
                return false;
            }
            setFileList([file]);
            return false; // Não fazer upload automático
        },
        onRemove: () => {
            setFileList([]);
        },
        fileList,
    };

    return (
        <Card title={
            <span>
                <ThunderboltOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
                Unidade Consumidora (Grid)
            </span>
        }>
            <Alert
                message="Esta etapa é obrigatória"
                description="Precisamos dos dados da conta de luz para calcular economia e consumo da rede."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={formData}
            >
                <Form.Item
                    label="Concessionária de Energia"
                    name="gridConcessionaria"
                    rules={[{ required: true, message: 'Concessionária é obrigatória' }]}
                    extra={`${distribuidoras.length} distribuidoras cadastradas (dados oficiais ANEEL)`}
                >
                    <Select 
                        placeholder="Digite para buscar ou selecione"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                    >
                        {distribuidoras.map((dist) => (
                            <Option key={dist.sigla} value={dist.sigla}>
                                <Space>
                                    <Tag color="blue">{dist.estado}</Tag>
                                    <span>{dist.nome}</span>
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Número da Conta de Luz / Instalação"
                    name="gridNumeroInstalacao"
                    rules={[{ required: true, message: 'Número da instalação é obrigatório' }]}
                    extra="Número que identifica a unidade consumidora na conta de luz"
                >
                    <Input placeholder="Ex: 1234567890" size="large" />
                </Form.Item>

                <Form.Item
                    label="Upload da Conta de Luz (PDF)"
                    extra="Importante: Use o PDF gerado diretamente no site da concessionária para garantir que todos os dados estejam visíveis e legíveis."
                    required
                >
                    <Upload {...uploadProps} maxCount={1}>
                        <Button 
                            icon={<UploadOutlined />} 
                            size="large"
                            style={{ width: '100%' }}
                        >
                            <FileTextOutlined /> Selecionar PDF da Conta de Luz
                        </Button>
                    </Upload>
                    {fileList.length > 0 && (
                        <Alert
                            message={`Arquivo selecionado: ${fileList[0].name}`}
                            type="success"
                            showIcon
                            style={{ marginTop: '12px' }}
                        />
                    )}
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: '24px', textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onBack}>
                            Voltar
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit"
                            icon={<RightOutlined />}
                            size="large"
                            style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                        >
                            Próximo: Carregadores
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default Step4Grid;
